import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Cart.css";
import useCheckoutStore from "../store/useCheckoutStore";
import { fetchExtrasByIds, buildExtrasMap } from "../services/extrasService";
import {
  checkCheckoutAllowed,
  canProceedCheckout,
} from "../utils/runWithGuards";

// Helpers
const priceToNumber = (v) =>
  v == null
    ? 0
    : (typeof v === "number"
        ? v
        : parseFloat(String(v).replace(",", ".").trim())) || 0;

const cleanId = (v) => (v == null ? "" : String(v).trim());

const Cart = ({
  ingredientsMap,
  extrasMap: extrasMapProp = {}, // allow parent-provided map as fallback
  minOrderAmount,
  deliveryFee,
  freeDeliveryThreshold, // may be 0 (meaning: no free delivery)
  onClose,
  isShopOpen,
  userAddressShort,
}) => {
  const navigate = useNavigate();
  const cartItems = useCheckoutStore((s) => s.cartItems);
  const setCartItems = useCheckoutStore((s) => s.setCartItems);

  // ---- Local extras map (fetched in Cart) ----
  const [extrasDocs, setExtrasDocs] = useState([]);
  const [loadingExtras, setLoadingExtras] = useState(false);
  const extrasMapLocal = useMemo(
    () => buildExtrasMap(extrasDocs),
    [extrasDocs]
  );

  // Prefer local map if we have it; fall back to prop
  const extrasMap = Object.keys(extrasMapLocal).length
    ? extrasMapLocal
    : extrasMapProp;

  // Collect unique extra IDs from cart
  const extraIdsInCart = useMemo(() => {
    const all = [];
    for (const it of cartItems) {
      const val = it?.selectedExtra;
      if (!val) continue;
      if (Array.isArray(val)) {
        for (const v of val) if (v) all.push(cleanId(v));
      } else {
        all.push(cleanId(val));
      }
    }
    return Array.from(new Set(all.filter(Boolean)));
  }, [cartItems]);

  // Fetch extras for IDs present in cart
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!extraIdsInCart.length) {
        if (alive) setExtrasDocs([]);
        return;
      }
      try {
        setLoadingExtras(true);
        const docs = await fetchExtrasByIds(extraIdsInCart);
        if (alive) setExtrasDocs(docs || []);
      } catch (e) {
        console.error("Failed to fetch extras in Cart:", e);
        if (alive) setExtrasDocs([]);
      } finally {
        if (alive) setLoadingExtras(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [extraIdsInCart]);

  const handleQuantityChange = (cartKey, delta) => {
    const updated = cartItems
      .map((it) =>
        it.cartKey === cartKey ? { ...it, quantity: it.quantity + delta } : it
      )
      .filter((it) => it.quantity > 0);
    setCartItems(updated);
  };

  const handleRemoveIngredient = (cartKey, ingredientId) => {
    const updated = cartItems.map((it) =>
      it.cartKey === cartKey
        ? {
            ...it,
            selectedIngredients: it.selectedIngredients.filter(
              (id) => id !== ingredientId
            ),
          }
        : it
    );
    setCartItems(updated);
  };

  // price already includes base + ingredients + extra (unit price)
  const calculateItemTotal = (item) =>
    (parseFloat(item.price) || 0) * item.quantity;

  // ---- Totals / Fees ----
  const totalAmount = cartItems.reduce(
    (acc, it) => acc + calculateItemTotal(it),
    0
  );

  // Treat 0 or non-positive threshold as "disabled"
  const hasFreeThreshold =
    Number.isFinite(freeDeliveryThreshold) && freeDeliveryThreshold > 0;

  const computedDeliveryFee =
    hasFreeThreshold && totalAmount >= freeDeliveryThreshold
      ? 0
      : deliveryFee || 0;

  const finalTotal = (totalAmount + computedDeliveryFee).toFixed(2);

  // Guard min order
  const minOrder = Number.isFinite(minOrderAmount) ? minOrderAmount : 0;

  const handleCheckout = () => {
    onClose?.();
    navigate("/checkout");
  };
  const { allowed, reason } = checkCheckoutAllowed({
    isShopOpen,
    userAddress: userAddressShort,
    hasItems: cartItems?.length > 0,
    minOrderAmount: minOrder,
    cartTotal: totalAmount,
  });
  return (
    <div>
      <div className="shop-cart-empty">
        <div className="shop-cart-empty-description">
          {cartItems.length === 0 ? (
            <p>Twój koszyk jest pusty.</p>
          ) : (
            <>
              <ul className="mobile-cart-items">
                {cartItems.map((item) => {
                  // Normalize to array to be future-proof (single- or multi-select)
                  const selectedExtraIds = Array.isArray(item.selectedExtra)
                    ? item.selectedExtra
                    : item.selectedExtra
                    ? [item.selectedExtra]
                    : [];

                  // Resolve extras for display
                  const resolvedExtras = selectedExtraIds
                    .map((id) => cleanId(id))
                    .map((id) => extrasMap[id])
                    .filter(Boolean);

                  // For label: eg. "Duża, Sos czosnkowy"
                  const extraLabel =
                    resolvedExtras.length > 0
                      ? resolvedExtras
                          .map((ex) => ex?.name?.pl || "Dodatek")
                          .join(", ")
                      : "";

                  return (
                    <li key={item.cartKey} className="mobile-cart-item">
                      <div className="mobile-cart-item-wrapper">
                        <span className="mobile-cart-item-quant">
                          <b>{item.quantity}x </b>
                        </span>

                        <div className="mobile-cart-item-title">
                          <p className="mobile-cart-item-name">
                            {item.name?.pl} - {Number(item.price).toFixed(2)} zł
                          </p>
                          <span className="mobile-cart-item-subitem">
                            {item.selectedSize}
                          </span>
                          <span className="mobile-cart-item-subitem">
                            {extraLabel ? `, ${extraLabel}` : ""}
                          </span>
                        </div>

                        <div className="cart-box-buttons">
                          <button
                            className="quantity-btn mobile-cart-quantity-btn"
                            onClick={() =>
                              handleQuantityChange(item.cartKey, -1)
                            }
                          >
                            -
                          </button>
                          <button
                            className="quantity-btn mobile-cart-quantity-btn"
                            onClick={() =>
                              handleQuantityChange(item.cartKey, 1)
                            }
                          >
                            +
                          </button>
                        </div>
                      </div>

                      {/* Ingredients */}
                      {item.selectedIngredients?.length > 0 && (
                        <ul className="cart-ingredients">
                          {item.selectedIngredients.map((id) => {
                            const ing = ingredientsMap[id];
                            if (!ing) return null;
                            return (
                              <li
                                key={id}
                                className="cart-ingredient-line-item"
                              >
                                <span>
                                  {item.quantity} × {ing.name?.pl} (+
                                  {priceToNumber(ing.price).toFixed(2)} zł)
                                </span>
                                <button
                                  onClick={() =>
                                    handleRemoveIngredient(item.cartKey, id)
                                  }
                                >
                                  ✖
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}

                      {/* If you want extras as line items, uncomment:
                      {resolvedExtras.length > 0 && (
                        <ul className="cart-ingredients">
                          {resolvedExtras.map((ex) => {
                            const exPrice =
                              ex?._priceNumber != null
                                ? ex._priceNumber
                                : priceToNumber(ex?.price);
                            return (
                              <li key={ex.id} className="cart-ingredient-line-item">
                                <span>
                                  {item.quantity} × {ex.name?.pl} (+{exPrice.toFixed(2)} zł)
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      */}
                    </li>
                  );
                })}
              </ul>

              <div className="shop-cart-calculation">
                <p className="shop-cart-subtotal">
                  <span>Suma częściowa:</span>
                  <span>{totalAmount.toFixed(2)} zł</span>
                </p>
                <p className="shop-cart-delivery-fee">
                  <span>Koszt dostawy:</span>
                  <span>{computedDeliveryFee.toFixed(2)} zł</span>
                </p>
                <p className="shop-cart-total">
                  <span>Razem:</span>
                  <span>{finalTotal} zł</span>
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="shop-cart-surcharge">
        <button
          className={`shop-cart-checkout ${!allowed ? "disabled" : ""}`}
          disabled={!allowed}
          title={!allowed ? reason : ""}
          onClick={() => {
            if (
              !canProceedCheckout({
                isShopOpen,
                userAddress: userAddressShort,
                hasItems: cartItems?.length > 0,
                minOrderAmount: minOrder,
                cartTotal: totalAmount,
              })
            )
              return;
            handleCheckout(); // safe to proceed
          }}
        >
          {allowed
            ? "Go to checkout"
            : reason ||
              `Minimalna wartość zamówienia: ${minOrder.toFixed(2)} zł`}
        </button>

        {hasFreeThreshold && (
          <p className="shop-cart-free-delivery">
            {totalAmount >= freeDeliveryThreshold ? (
              <span
                className="shop-cart-free-delivery-qualify"
                style={{ color: "green" }}
              >
                Gratulacje! Otrzymujesz darmową dostawę.
              </span>
            ) : (
              `Zamów za ${(freeDeliveryThreshold - totalAmount).toFixed(
                2
              )} zł więcej, aby otrzymać darmową dostawę.`
            )}
          </p>
        )}
      </div>
    </div>
  );
};

export default Cart;
